import React, { useState, useEffect, useRef, memo, useCallback, forwardRef, useImperativeHandle } from 'react';
import SpotifyWebPlayer from 'react-spotify-web-playback';
import YouTube from 'react-youtube';
import { Play, Pause, AlertCircle } from 'lucide-react';
import { isAudioUnlocked, forcePlay } from '../utils/audioUnlock';

// Memoized SoundCloud Player with Widget API for track end detection
// Uses forwardRef to expose play/pause methods to parent
const SoundCloudPlayer = memo(forwardRef(({ trackUrl, onEnded, autoPlay = true }, ref) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  // Use ref for callback to prevent re-creating iframe when callback changes
  const onEndedRef = useRef(onEnded);
  
  // Keep the ref updated
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);
  
  // Expose play/pause methods to parent via ref
  useImperativeHandle(ref, () => ({
    play: () => {
      if (widgetRef.current) {
        console.log('SoundCloud: play() called via ref');
        widgetRef.current.play();
      }
    },
    pause: () => {
      if (widgetRef.current) {
        console.log('SoundCloud: pause() called via ref');
        widgetRef.current.pause();
      }
    },
    getWidget: () => widgetRef.current
  }), []);

  useEffect(() => {
    if (!trackUrl || !containerRef.current) return;
    
    console.log('Loading SoundCloud player with URL:', trackUrl);
    
    // Load SoundCloud Widget API — preloaded in index.html, this just waits for it
    const loadWidgetAPI = () => {
      return new Promise((resolve) => {
        if (window.SC) {
          resolve(window.SC);
          return;
        }
        // API script is preloaded via index.html <script async>, just wait for it
        const check = () => window.SC ? resolve(window.SC) : setTimeout(check, 50);
        check();
      });
    };
    
    // Create iframe manually
    const iframe = document.createElement('iframe');
    iframe.id = 'soundcloud-widget-' + Date.now();
    iframe.width = '100%';
    iframe.height = '166';
    iframe.scrolling = 'no';
    iframe.frameBorder = 'no';
    iframe.allow = 'autoplay';
    iframe.loading = 'eager';
    // CRITICAL: single_active=true prevents auto-advancing to related tracks
    // show_playcount=false, show_artwork=true for cleaner look
    iframe.src = `https://w.soundcloud.com/player/?url=${encodeURIComponent(trackUrl)}&color=%23f2d857&auto_play=${autoPlay}&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true&single_active=true`;
    
    const container = containerRef.current;
    
    if (container) {
      container.innerHTML = '';
      container.appendChild(iframe);
      iframeRef.current = iframe;
      
      // Initialize Widget API after iframe loads
      loadWidgetAPI().then((SC) => {
        if (!SC || !iframeRef.current) return;
        
        try {
          const widget = SC.Widget(iframeRef.current);
          widgetRef.current = widget;
          
          // Bind to FINISH event (track ended)
          widget.bind(SC.Widget.Events.FINISH, () => {
            console.log('SoundCloud track finished');
            if (onEndedRef.current) {
              onEndedRef.current();
            }
          });
          
          // Bind to READY event
          widget.bind(SC.Widget.Events.READY, () => {
            console.log('SoundCloud widget ready, autoPlay:', autoPlay, 'audioUnlocked:', isAudioUnlocked());
            
            if (autoPlay) {
              // SoundCloud iframe handles its own audio context
              // Always attempt play when autoPlay is true - mobile browsers may have
              // unlocked audio via user gesture even if our flag isn't set yet
              console.log('SoundCloud: Calling play() on READY');
              widget.play();
              
              // Multi-stage verification for mobile - SoundCloud can be finicky
              // Check at 300ms, 800ms, and 1500ms to ensure playback started
              const verifyAndPlay = (attempt) => {
                widget.isPaused((paused) => {
                  widget.getPosition((pos) => {
                    console.log(`SoundCloud: Verify attempt ${attempt} - paused: ${paused}, position: ${pos}ms`);
                    if (paused || pos < 100) {
                      console.log('SoundCloud: Playback not confirmed, retrying play()');
                      widget.play();
                    }
                  });
                });
              };
              
              setTimeout(() => verifyAndPlay(1), 300);
              setTimeout(() => verifyAndPlay(2), 800);
              setTimeout(() => verifyAndPlay(3), 1500);
            }
          });
          
          // Bind to PLAY event to confirm playback started
          widget.bind(SC.Widget.Events.PLAY, () => {
            console.log('SoundCloud: PLAY event - playback confirmed');
          });
          
          // Bind to ERROR event
          widget.bind(SC.Widget.Events.ERROR, (e) => {
            console.error('SoundCloud error:', e);
          });
        } catch (err) {
          console.error('Failed to initialize SoundCloud widget:', err);
        }
      });
    }
    
    // Cleanup function
    return () => {
      if (widgetRef.current) {
        try {
          widgetRef.current.unbind(window.SC?.Widget?.Events?.FINISH);
          widgetRef.current.unbind(window.SC?.Widget?.Events?.READY);
          widgetRef.current.unbind(window.SC?.Widget?.Events?.ERROR);
        } catch (err) {
          // Ignore cleanup errors
        }
        widgetRef.current = null;
      }
      if (iframeRef.current && container && container.contains(iframeRef.current)) {
        try {
          container.removeChild(iframeRef.current);
        } catch (err) {
          console.log('Iframe already removed');
        }
      }
      iframeRef.current = null;
    };
  }, [trackUrl, autoPlay]); // Removed onEnded - using ref instead to prevent iframe refresh

  return (
    <div 
      ref={containerRef} 
      id="soundcloud-player-container"
      style={{ width: '100%', minHeight: '166px', background: '#1a1a1a', borderRadius: '8px' }}
    />
  );
}));

SoundCloudPlayer.displayName = 'SoundCloudPlayer';

// eslint-disable-next-line no-unused-vars
const UnifiedMusicPlayer = ({ track, onTrackEnd, isReviewer = false, autoPlay = false, compactMode = false, externalIsPlaying = null }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [spotifyToken, setSpotifyToken] = useState(null);
  
  const audioRef = useRef(null);
  const youtubeRef = useRef(null);
  const soundcloudWidgetRef = useRef(null);

  const API_URL = '';  // Use relative URLs for production compatibility

  const fetchSpotifyToken = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/oauth/spotify/token`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSpotifyToken(data.access_token);
      } else {
        setError('Spotify not connected. Please connect Spotify in your dashboard.');
      }
    } catch (err) {
      console.error('Error fetching Spotify token:', err);
      setError('Failed to connect to Spotify');
    }
  };

  // Track the previous track to detect changes
  const prevTrackRef = useRef(null);
  const currentTrackKey = track ? `${track.platform}-${track.platform_id || track.streaming_link || track.file_url}` : null;
  const trackChanged = currentTrackKey !== prevTrackRef.current;
  const autoPlayTriggeredRef = useRef(false);
  
  // ============================================================
  // SPOTIFY AUTO-ADVANCE: Robust implementation
  // ============================================================
  // KEY INSIGHT: The callback doesn't fire continuously during playback!
  // It only fires on: play/pause, seek, track change, and when track ends.
  // 
  // SOLUTION: Track when playback STARTED (timestamp) and use time-based detection.
  // When track ends: isPaused + position near 0 + significant time elapsed since start
  // 
  // This works even if the callback never fires during playback (no seek/pause).
  const spotifyPlaybackStartTimeRef = useRef(null);  // When playback started (Date.now())
  const spotifyTrackEndHandledRef = useRef(false);  // Prevent double-firing
  const spotifyTrackIdRef = useRef(null);  // Current track ID to detect track changes
  const spotifyWasPlayingRef = useRef(false);  // Track previous playing state
  const spotifyLastKnownProgressRef = useRef(0);  // Last progress we saw (for logging)
  
  // Update expected track when parent changes track
  useEffect(() => {
    if (track) {
      // Reset all Spotify tracking state when track changes
      spotifyPlaybackStartTimeRef.current = null;
      spotifyTrackEndHandledRef.current = false;
      spotifyTrackIdRef.current = track.platform_id || track.id;
      spotifyWasPlayingRef.current = false;
      spotifyLastKnownProgressRef.current = 0;
      console.log('[Spotify] Track changed, reset state for:', spotifyTrackIdRef.current);
    }
  }, [track]);
  
  // Reset state when track changes (using ref comparison)
  useEffect(() => {
    if (trackChanged && currentTrackKey) {
      prevTrackRef.current = currentTrackKey;
      autoPlayTriggeredRef.current = false; // Reset autoplay trigger flag
      setCurrentTime(0);
      setError(null);
      
      // Load Spotify token if needed
      if (track?.platform === 'spotify') {
        fetchSpotifyToken();
      }
      
      // For non-file platforms, set isPlaying immediately if autoPlay is enabled
      // File uploads will be handled separately after the audio element mounts
      if (autoPlay && track?.platform !== 'file') {
        setIsPlaying(true);
        console.log('Auto-play enabled for', track?.platform);
      } else if (!autoPlay) {
        setIsPlaying(false);
      }
    }
  }, [currentTrackKey, trackChanged, autoPlay, track?.platform]);

  // Separate effect for file upload autoplay - runs when audioRef becomes available
  useEffect(() => {
    if (autoPlay && track?.platform === 'file' && !autoPlayTriggeredRef.current) {
      const attemptPlay = async () => {
        if (audioRef.current) {
          autoPlayTriggeredRef.current = true;
          console.log('Attempting file upload autoplay, audioUnlocked:', isAudioUnlocked());
          
          // Use forcePlay helper which leverages the unlocked AudioContext
          const success = await forcePlay(audioRef.current);
          if (success) {
            setIsPlaying(true);
            console.log('File upload auto-playing successfully');
          } else {
            console.warn('File upload autoplay failed - user may need to click play');
            // Single retry after brief delay
            setTimeout(async () => {
              if (audioRef.current && audioRef.current.paused) {
                const retrySuccess = await forcePlay(audioRef.current);
                if (retrySuccess) {
                  setIsPlaying(true);
                }
              }
            }, 500);
          }
        } else {
          // Audio element not ready yet, try again shortly
          setTimeout(attemptPlay, 100);
        }
      };
      
      // Start attempt after a short delay to ensure DOM is ready
      setTimeout(attemptPlay, 150);
    }
  }, [autoPlay, track?.platform, currentTrackKey]);

  // ============================================================
  // EXTERNAL PLAY/PAUSE CONTROL
  // When externalIsPlaying prop changes, sync the player state
  // This allows parent components (like LiveMobileView) to control playback
  // ============================================================
  const prevExternalIsPlaying = useRef(externalIsPlaying);
  
  useEffect(() => {
    // Only react when externalIsPlaying is explicitly set (not null) and has changed
    if (externalIsPlaying === null || externalIsPlaying === prevExternalIsPlaying.current) {
      prevExternalIsPlaying.current = externalIsPlaying;
      return;
    }
    
    prevExternalIsPlaying.current = externalIsPlaying;
    
    // Skip if state already matches
    if (externalIsPlaying === isPlaying) {
      return;
    }
    
    console.log('External play control:', externalIsPlaying ? 'PLAY' : 'PAUSE', 'platform:', track?.platform);
    
    if (!track) return;
    
    if (track.platform === 'file') {
      if (audioRef.current) {
        if (externalIsPlaying) {
          audioRef.current.play().catch(e => console.error('File play failed:', e));
        } else {
          audioRef.current.pause();
        }
        setIsPlaying(externalIsPlaying);
      }
    } else if (track.platform === 'youtube') {
      // youtubeRef.current stores the player directly from onReady's event.target
      if (youtubeRef.current) {
        const player = youtubeRef.current;
        try {
          if (externalIsPlaying) {
            console.log('YouTube: External PLAY command');
            player.playVideo();
          } else {
            console.log('YouTube: External PAUSE command');
            player.pauseVideo();
          }
          setIsPlaying(externalIsPlaying);
        } catch (_e) {
          console.error('YouTube control failed:', e);
        }
      } else {
        console.log('YouTube: Player ref not available yet');
      }
    } else if (track.platform === 'soundcloud') {
      // SoundCloud widget - need to wait for it to be ready
      const attemptControl = () => {
        if (window.SC && soundcloudWidgetRef.current) {
          if (externalIsPlaying) {
            soundcloudWidgetRef.current.play();
            console.log('SoundCloud: External PLAY command sent');
          } else {
            soundcloudWidgetRef.current.pause();
            console.log('SoundCloud: External PAUSE command sent');
          }
          setIsPlaying(externalIsPlaying);
        } else {
          // Retry after widget loads
          setTimeout(attemptControl, 200);
        }
      };
      attemptControl();
    } else if (track.platform === 'spotify') {
      // Spotify is handled by its own component state
      setIsPlaying(externalIsPlaying);
    }
  }, [externalIsPlaying, track, isPlaying]);

  const handlePlayPause = () => {
    if (!track) return;

    if (track.platform === 'file') {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    } else if (track.platform === 'youtube') {
      // youtubeRef.current stores the player directly from onReady's event.target
      if (youtubeRef.current) {
        const player = youtubeRef.current;
        try {
          if (isPlaying) {
            console.log('YouTube: Pause via handlePlayPause');
            player.pauseVideo();
          } else {
            console.log('YouTube: Play via handlePlayPause');
            player.playVideo();
          }
          setIsPlaying(!isPlaying);
        } catch (_e) {
          console.error('YouTube play/pause failed:', e);
        }
      }
    } else if (track.platform === 'soundcloud') {
      if (soundcloudWidgetRef.current) {
        if (isPlaying) {
          soundcloudWidgetRef.current.pause();
        } else {
          soundcloudWidgetRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    }
    // Spotify handles its own play/pause through the component
  };

  const handleTimeUpdate = () => {
    if (track.platform === 'file' && audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);
    }
  };

  // Memoize handleEnded to prevent unnecessary re-renders of child players
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (onTrackEnd) {
      onTrackEnd();
    }
  }, [onTrackEnd]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPlatformIcon = () => {
    switch (track?.platform) {
      case 'spotify': return '🎵';
      case 'youtube': return '📺';
      case 'soundcloud': return '🔊';
      case 'file': return '🎧';
      default: return '🎵';
    }
  };

  // ============================================================
  // SPOTIFY CALLBACK HANDLER - The core auto-advance logic
  // ============================================================
  // PROBLEM: Callback doesn't fire continuously during playback!
  // SOLUTION: Time-based detection using playback start timestamp
  // 
  // When callback fires with isPlaying=true, we record the start time.
  // When callback fires with isPlaying=false + position~0, we check elapsed time.
  // If significant time elapsed (>10s or >80% of duration), track ended naturally.
  // ============================================================
  const handleSpotifyCallback = useCallback((state) => {
    // Extract values - use track.duration_ms as it's always valid (unlike state.duration)
    const currentTrackId = state.track?.id;
    const trackDurationMs = state.track?.duration_ms || 0;
    const progressMs = state.progressMs || state.position || 0;
    const isCurrentlyPlaying = state.isPlaying;
    const trackName = state.track?.name || 'Unknown';
    const wasPlaying = spotifyWasPlayingRef.current;
    
    // Calculate elapsed time since playback started
    const elapsedMs = spotifyPlaybackStartTimeRef.current 
      ? Date.now() - spotifyPlaybackStartTimeRef.current 
      : 0;
    
    // Calculate justPaused early so we can use it for logging
    const justPaused = wasPlaying && !isCurrentlyPlaying;
    
    // Log current state only on significant changes (not every callback)
    if (justPaused || (isCurrentlyPlaying && !wasPlaying)) {
      console.log('[Spotify]', isCurrentlyPlaying ? 'PLAYING' : 'PAUSED',
        `progress: ${Math.round(progressMs/1000)}s`,
        `duration: ${Math.round(trackDurationMs/1000)}s`,
        `track: ${trackName}`
      );
    }
    
    // Update last known progress (for logging/debugging)
    spotifyLastKnownProgressRef.current = progressMs;
    
    // ============================================================
    // TRACK CHANGE DETECTION
    // ============================================================
    if (currentTrackId && currentTrackId !== spotifyTrackIdRef.current) {
      console.log('[Spotify] Track ID changed from', spotifyTrackIdRef.current, 'to', currentTrackId);
      spotifyTrackIdRef.current = currentTrackId;
      spotifyPlaybackStartTimeRef.current = null;
      spotifyTrackEndHandledRef.current = false;
      spotifyWasPlayingRef.current = false;
    }
    
    // ============================================================
    // PLAYBACK START DETECTION
    // ============================================================
    // When playback starts (or resumes), record the timestamp
    if (isCurrentlyPlaying && !spotifyPlaybackStartTimeRef.current) {
      spotifyPlaybackStartTimeRef.current = Date.now() - progressMs; // Adjust for current position
      console.log('[Spotify] Playback started, tracking time from now');
    }
    
    // ============================================================
    // TRACK END DETECTION (TIME-BASED)
    // ============================================================
    // A track has ended when ALL of these are true:
    // 1. Was playing, now paused (state transition)
    // 2. Position is at or near 0 (track reset after ending)
    // 3. Significant time elapsed since playback started
    // 4. We haven't already handled this end event
    
    // justPaused is already calculated above for logging
    const positionNearZero = progressMs < 2000; // Within first 2 seconds
    
    // Calculate if significant time elapsed
    // Use either percentage of duration or absolute time
    let hadSignificantPlayTime = false;
    if (trackDurationMs > 0 && elapsedMs > 0) {
      // If we know duration, check if we played at least 80% of the track
      const percentElapsed = (elapsedMs / trackDurationMs) * 100;
      hadSignificantPlayTime = percentElapsed > 80 || elapsedMs > (trackDurationMs - 5000);
      
      if (justPaused && positionNearZero) {
        console.log('[Spotify] End check - elapsed:', Math.round(elapsedMs/1000) + 's', 
                    'duration:', Math.round(trackDurationMs/1000) + 's',
                    'percent:', Math.round(percentElapsed) + '%');
      }
    } else if (elapsedMs > 0) {
      // Fallback: If duration unknown, consider 30+ seconds as significant
      hadSignificantPlayTime = elapsedMs > 30000;
    }
    
    // Combined detection
    const trackEnded = justPaused && 
                       positionNearZero && 
                       hadSignificantPlayTime && 
                       !spotifyTrackEndHandledRef.current;
    
    if (trackEnded) {
      console.log('[Spotify] === TRACK ENDED DETECTED ===');
      console.log('[Spotify] Detection conditions:', {
        justPaused,
        positionNearZero,
        hadSignificantPlayTime,
        elapsedMs: Math.round(elapsedMs),
        trackDurationMs,
        percentElapsed: trackDurationMs > 0 ? Math.round((elapsedMs / trackDurationMs) * 100) : 'N/A'
      });
      
      // Mark as handled to prevent double-firing
      spotifyTrackEndHandledRef.current = true;
      
      // Trigger the playlist advance
      console.log('[Spotify] Calling handleEnded() to advance playlist');
      handleEnded();
    }
    
    // ============================================================
    // STATE UPDATES
    // ============================================================
    // Update previous playing state for next callback
    spotifyWasPlayingRef.current = isCurrentlyPlaying;
    
    // Reset for next track if playback starts fresh
    if (isCurrentlyPlaying && spotifyTrackEndHandledRef.current) {
      console.log('[Spotify] New playback detected, resetting end handler flag');
      spotifyTrackEndHandledRef.current = false;
      spotifyPlaybackStartTimeRef.current = Date.now() - progressMs;
    }
    
    // Update React state
    setIsPlaying(isCurrentlyPlaying);
  }, [handleEnded]);

  if (!track) {
    return (
      <div className="bg-[#111] border border-[#F2D857]/20 rounded-lg p-6 text-center text-[#888888]">
        No track selected
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#111] border border-red-500/30 rounded-lg p-6">
        <div className="flex items-center gap-3 text-red-500">
          <AlertCircle className="w-6 h-6" />
          <div>
            <div className="font-bold">Playback Error</div>
            <div className="text-sm">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Compact mode for Live Mobile - just show the embedded player
  if (compactMode) {
    return (
      <div className="bg-transparent rounded-lg">
        {/* Platform-Specific Player - Minimal */}
        <div className="w-full">
          {track.platform === 'spotify' && spotifyToken && track.platform_id && (
            <SpotifyWebPlayer
              token={spotifyToken}
              uris={[`spotify:track:${track.platform_id}`]}
              play={autoPlay}
              persistDeviceSelection={true}
              callback={handleSpotifyCallback}
              styles={{
                bgColor: '#0a0a0a',
                color: '#fff',
                loaderColor: '#F2D857',
                sliderColor: '#F2D857',
                trackArtistColor: '#888',
                trackNameColor: '#fff',
                height: 80,
              }}
            />
          )}

          {track.platform === 'youtube' && track.platform_id && (
            <div className="youtube-player-container" style={{ width: '100%' }}>
              <YouTube
                videoId={track.platform_id}
                opts={{
                  width: '100%',
                  height: '320',
                  playerVars: {
                    autoplay: autoPlay ? 1 : 0,
                    controls: 1,
                    modestbranding: 1,
                    rel: 0,
                    playsinline: 1,
                    fs: 0,
                    iv_load_policy: 3,
                  },
                }}
                onReady={(event) => {
                  youtubeRef.current = event.target;
                  console.log('YouTube compact ready, autoPlay:', autoPlay);
                  if (autoPlay) {
                    event.target.playVideo();
                  }
                }}
                onStateChange={(event) => {
                  if (event.data === 0) {
                    console.log('YouTube compact: Video ended - advancing to next track');
                    handleEnded();
                  } else if (event.data === 1) setIsPlaying(true);
                  else if (event.data === 2) setIsPlaying(false);
                }}
                onError={(error) => {
                  console.error('YouTube compact error:', error);
                  setError('Failed to load YouTube video');
                }}
              />
            </div>
          )}

          {track.platform === 'soundcloud' && track.streaming_link && (
            <div style={{ height: '80px', overflow: 'hidden' }}>
              <SoundCloudPlayer 
                ref={soundcloudWidgetRef}
                key={track.streaming_link}
                trackUrl={track.streaming_link}
                onEnded={handleEnded}
                autoPlay={autoPlay}
              />
            </div>
          )}

          {track.platform === 'file' && track.file_url && (
            <audio
              ref={audioRef}
              src={track.file_url.startsWith('http') ? track.file_url : `${API_URL}${track.file_url}`}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              autoPlay={autoPlay}
              playsInline
              onLoadedMetadata={(e) => setDuration(e.target.duration)}
              onCanPlay={() => {
                // Safari: force play when audio is ready
                if (autoPlay && audioRef.current && audioRef.current.paused) {
                  audioRef.current.play().catch(e => console.log('Audio autoplay:', e.name));
                }
              }}
              onError={() => setError('Failed to load audio file')}
              controls
              className="w-full h-12"
              style={{ maxHeight: '48px' }}
            />
          )}
        </div>
        <p className="text-xs text-center text-gray-500 mt-1">
          🎵 {track.title} - {track.artist}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#111] border border-[#F2D857]/30 rounded-lg p-6">
      {/* Track Info */}
      <div className="flex items-center gap-4 mb-4">
        {track.artwork_url || track.cover_art ? (
          <img 
            src={track.artwork_url || track.cover_art} 
            alt={track.title}
            className="w-16 h-16 rounded object-cover"
          />
        ) : (
          <div className="w-16 h-16 bg-[#F2D857] rounded flex items-center justify-center text-3xl">
            {getPlatformIcon()}
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-xl font-bold text-white">{track.title}</h3>
          <p className="text-[#888888]">{track.enhanced_artist || track.artist}</p>
          <div className="flex items-center gap-2 text-sm text-[#F2D857] mt-1">
            <span>{getPlatformIcon()}</span>
            <span className="capitalize">{track.platform || 'unknown'}</span>
            {track.duration && <span>• {track.duration}</span>}
          </div>
        </div>
      </div>

      {/* Platform-Specific Player */}
      <div className="mb-4">
        {track.platform === 'spotify' && spotifyToken && track.platform_id && (
          <SpotifyWebPlayer
            token={spotifyToken}
            uris={[`spotify:track:${track.platform_id}`]}
            play={autoPlay}
            persistDeviceSelection={true}
            callback={handleSpotifyCallback}
            styles={{
              bgColor: '#111',
              color: '#fff',
              loaderColor: '#F2D857',
              sliderColor: '#F2D857',
              trackArtistColor: '#888',
              trackNameColor: '#fff',
            }}
          />
        )}

        {track.platform === 'youtube' && track.platform_id && (
          <div className="youtube-player-container">
            <YouTube
              videoId={track.platform_id}
              opts={{
                height: '600',
                width: '100%',
                playerVars: {
                  autoplay: autoPlay ? 1 : 0,
                  controls: 1,
                  modestbranding: 1,
                  rel: 0,            // Disable related videos at end
                  playsinline: 1,    // Required for iOS Safari
                  fs: 0,             // Disable fullscreen to prevent navigation
                },
              }}
              onReady={(event) => {
                console.log('YouTube player ready, autoPlay:', autoPlay);
                youtubeRef.current = event.target;
                if (autoPlay) {
                  try {
                    event.target.playVideo();
                    console.log('YouTube: playVideo() called');
                  } catch (error) {
                    console.error('Error auto-playing YouTube video:', error);
                  }
                }
              }}
              onStateChange={(event) => {
                console.log('YouTube state changed:', event.data);
                // 0 = ended - advance to OUR next track
                if (event.data === 0) {
                  console.log('YouTube: Video ended - advancing to next track');
                  handleEnded();
                } else if (event.data === 1) {
                  setIsPlaying(true);
                } else if (event.data === 2) {
                  setIsPlaying(false);
                }
              }}
              onError={(error) => {
                console.error('YouTube player error:', error);
                setError('Failed to load YouTube video. Please check the video ID.');
              }}
            />
          </div>
        )}

        {track.platform === 'soundcloud' && track.streaming_link && (
          <SoundCloudPlayer 
            ref={soundcloudWidgetRef}
            key={track.streaming_link}
            trackUrl={track.streaming_link}
            onEnded={handleEnded}
            autoPlay={autoPlay}
          />
        )}

        {track.platform === 'file' && track.file_url && (
          <div>
            <audio
              ref={audioRef}
              src={track.file_url.startsWith('http') ? track.file_url : `${API_URL}${track.file_url}`}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              autoPlay={autoPlay}
              playsInline
              onLoadedMetadata={(e) => {
                console.log('Audio loaded:', e.target.duration);
                setDuration(e.target.duration);
              }}
              onCanPlay={() => {
                console.log('Audio can play, autoPlay:', autoPlay, 'paused:', audioRef.current?.paused);
                // Safari: force play when audio is ready
                if (autoPlay && audioRef.current && audioRef.current.paused) {
                  audioRef.current.play().catch(e => console.log('Audio autoplay onCanPlay:', e.name));
                }
              }}
              onError={async (e) => {
                console.error('[P1] Audio playback error:', e);
                const audioSrc = track.file_url.startsWith('http') ? track.file_url : `${API_URL}${track.file_url}`;
                console.error('[P1] Audio src:', audioSrc);
                
                // P1: Try to fetch error details from backend
                try {
                  const response = await fetch(audioSrc, { method: 'HEAD' });
                  if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    const errorCode = errorData?.error_code || response.status;
                    
                    // P1: Differentiated error messages based on error code
                    switch (errorCode) {
                      case 'AUDIO_NOT_FOUND':
                      case 404:
                        setError('Audio file not found. The track may need to be re-uploaded by the artist.');
                        break;
                      case 'RANGE_NOT_SATISFIABLE':
                      case 416:
                        setError('Audio seeking error. Try refreshing the page and playing from the start.');
                        break;
                      case 'AUDIO_READ_ERROR':
                      case 500:
                        setError('Server error loading audio. Please try again in a few moments.');
                        break;
                      case 'ACCESS_DENIED':
                      case 403:
                        setError('Access denied. You may not have permission to play this track.');
                        break;
                      default:
                        // Check if this is a legacy local file URL that needs re-upload
                        if (track.file_url.includes('/api/uploads/audio/') && !track.audio_gridfs_id) {
                          setError('This audio file needs to be re-uploaded. The original file is no longer available.');
                        } else {
                          setError('Failed to load audio file. Please check the file format or try again.');
                        }
                    }
                  } else {
                    // Server returned OK but browser couldn't play - likely format issue
                    setError('Unable to play audio. The file format may not be supported by your browser.');
                  }
                } catch (fetchErr) {
                  console.error('[P1] Error checking audio status:', fetchErr);
                  // Network error - show generic message
                  setError('Network error loading audio. Please check your connection and try again.');
                }
              }}
              preload="auto"
              controls
              className="w-full mb-4"
            />
            
            {/* Custom audio controls */}
            <div className="space-y-3">
              {/* Progress bar */}
              <div className="relative h-2 bg-[#333] rounded-full overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full bg-[#F2D857] transition-all"
                  style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                />
              </div>
              
              {/* Time display */}
              <div className="flex justify-between text-sm text-[#888888]">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
              
              {/* Play/Pause button */}
              <div className="flex justify-center">
                <button
                  onClick={handlePlayPause}
                  className="w-14 h-14 bg-[#F2D857] rounded-full flex items-center justify-center hover:bg-[#F2D857]/90 transition"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 text-black" />
                  ) : (
                    <Play className="w-6 h-6 text-black ml-1" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error handling for invalid/removed tracks */}
      {track.platform === 'spotify' && !track.platform_id && (
        <div className="text-red-500 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Invalid Spotify track URL
        </div>
      )}
      
      {track.platform === 'youtube' && !track.platform_id && (
        <div className="text-red-500 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Invalid YouTube video URL
        </div>
      )}
    </div>
  );
};

export default UnifiedMusicPlayer;
